import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { Card, CardMedia, IconButton, Stack } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Konva from 'konva';
import { Rect } from 'konva/lib/shapes/Rect';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { EMaterialCategory, ENumberOfPropellers, EPropellerDirectionWithRespectToTheQuay, ETypeOfPropeller, ETypeOfQuay, EUnitOfMeasure } from '../../../shared/enum';
import { setEngine } from '../../../store/engine/engineSlice';
import { EReduxState } from '../../../store/enum';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { selectProject } from '../../../store/project/projectSlice';
import { selectResult } from '../../../store/result/resultSlice';
import { theme } from '../../../style';
import { useWindowSize } from './Hook';
import containerParallel from './container_parallel.svg';
import containerTransversal from './container_transversal.svg';
import Grid from './grid_pattern.svg';
import patternEarth from './patternEarth.svg';
import propellerFront from './propeller_front.gif';
import propellerLat from './propeller_lat.gif';
import patternQuay from './quay_pattern.svg';
import shipParallel from './ship_parallel.svg';
import shipTransversal from './ship_transversal.svg';
import structureOverQuayImg from './structure_over_quay.svg';
import { getDistanceFromVertex, getImageFromScenario, getTextureByMaterial, loadGif, loadImages } from './util';
import { degreesToRadians } from '../../../shared/util';

interface INewEngineProps {
    index: number;
    export?: boolean;
}

export interface ISrcImage {
    patternQuay: string | HTMLImageElement;
    patternEarth: string | HTMLImageElement;
    patternMaterial?: string | HTMLImageElement;
    patternDoubleMaterial?: string | HTMLImageElement;
    structureOverQuayImg: string | HTMLImageElement;
    shipTransversal: string | HTMLImageElement;
    shipParallel: string | HTMLImageElement;
    containerParallel: string | HTMLImageElement;
    containerTransversal: string | HTMLImageElement;
}

const color = {
    water: '#CCDBFC',
    waterFlow: 'blue',
    lineWater: '#014AEF',
    piles: '#29646E',
    structure: '#183F4B',
    backgroundText: 'rgb(245, 245, 245, 0.7)',
    lineIndicator: 'black',
    text: 'black',
};

var layer = new Konva.Layer();
const canvasPropellerFront = loadGif(propellerFront, layer);
const canvasPropellerLat = loadGif(propellerLat, layer);

const NewEngine: React.FunctionComponent<INewEngineProps> = props => {
    var stage: Konva.Stage;
    var scaleBy: number;
    var group: Konva.Group;

    let startFirstPileX: number;
    let startShipX: number;
    let startEngineX: number;
    let startStructureOverQuayX: number;
    let startMaterialX: number;
    let startQuayX: number;
    let startZoneOfErosionX: number;
    let zoneOfErosionWidth: number;

    const { t } = useTranslation();

    const sizeWindow = useWindowSize();
    const matches = useMediaQuery(`(min-width:${theme.breakpoints.values.md}px)`);

    const project = useAppSelector(selectProject);
    const result = useAppSelector(selectResult);
    const dispatch = useAppDispatch();

    const mToPixel = 20;
    const fttToPixel = 0;

    const convertToPixel = project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? mToPixel : fttToPixel;

    const designWaterLevel = project.data?.geometry?.designWaterLevel!;
    const designDredgingLevel = project.data?.geometry?.designDredgingLevel!;
    const propellerDiameter = project.data?.engines![props.index].propellerDiameter! * convertToPixel;
    const propellerDistanceFromQuayL = project.data?.scenarios![props.index].propellerDistanceFromQuayL! * convertToPixel;
    const draught = project.data?.scenarios![props.index].draft! * convertToPixel;
    const widthOfTheProtectionW = project.data?.material?.physicalData![0].widthOfTheProtectionW! * convertToPixel;
    const thicknessT = project.data?.material?.physicalData[0].thickness! * convertToPixel;
    const doubleMaterialthicknessT = project.data?.doubleLayerMaterial?.physicalData[0].thickness! * convertToPixel;
    const minimumWaterDepth = project.data?.geometry?.minimumWaterDepth! * convertToPixel;
    const typeOfQuay: ETypeOfQuay = project.data!.geometry!.typeOfQuay!;
    const typeOfPropeller: ETypeOfPropeller = project.data!.engines![props.index].typeOfPropeller;
    const propellerDirectionWithRespectToTheQuay: EPropellerDirectionWithRespectToTheQuay = project.data!.engines![props.index].propellerDirectionWithRespectToTheQuay;
    const numberOfPropellers: ENumberOfPropellers = project.data!.engines![props.index].numberOfPropellers;
    const distanceBetweenVesselAndQuayBquay = project.data?.geometry?.distanceBetweenVesselAndQuay! * convertToPixel;
    const vesselBeamBs = project.data?.scenarios![props.index].vesselBeamBs! * convertToPixel;
    const quaySlope = project.data?.geometry?.quaySlope!;
    const distanceBetweenPileAxis = project.data?.geometry?.distanceBetweenPileAxis! * convertToPixel;
    const pileDiameter = project.data?.geometry?.pileDiameter! * convertToPixel;
    const horizontalDistanceBetweenPropellerAxisAndTheSlope = project.data?.scenarios![props.index].horizontalDistanceBetweenPropellerAxisAndTheSlopeToe! * convertToPixel;
    const verticalDistanceBetweenTheAxisOfThePropellerAndTheKell = project.data?.scenarios![props.index].verticalDistanceBetweenTheAxisOfThePropellerAndTheKell! * convertToPixel;

    const underKeelClearance = result.data?.flowVelocityAtThePropellers ? result.data?.flowVelocityAtThePropellers![props.index].underKeelClearance! * convertToPixel : 0;
    const slopeAbscissaOfMaximumCurrentVelocityXvMax = result.data?.openPiledQuayScenarios
        ? result.data?.openPiledQuayScenarios[props.index].slopeAbscissaOfMaximumCurrentVelocityXvMax! * convertToPixel
        : result.data?.propellerAxisTransverseToSlopedQuayStructureScenarios
        ? result.data?.propellerAxisTransverseToSlopedQuayStructureScenarios[props.index].slopeAbscissaOfMaximumCurrentVelocityXvMax! * convertToPixel
        : 0;

    const groundHeight = 100;
    const engineWidth = propellerDiameter;
    const quayHorizontalWidth = 100;
    const offsetMaterial = 400; // Distance between start canvas and material
    const offsetOverQuay = 40; // Distance between start canvas and quay
    const quayOverWaterHeight = 40; // Distance between start quay and water
    const overWaterHeight = offsetOverQuay + quayOverWaterHeight;
    const slopeLengthUnderWater = minimumWaterDepth / Math.sin(degreesToRadians(quaySlope));
    const slopeLengthUnderWaterX = slopeLengthUnderWater * Math.cos(degreesToRadians(quaySlope));
    const slopeLengthUnderWaterY = slopeLengthUnderWater * Math.sin(degreesToRadians(quaySlope));
    const slopeLengthOverWater = quayOverWaterHeight / Math.sin(degreesToRadians(quaySlope));
    const slopeLengthOverWaterX = slopeLengthOverWater * Math.cos(degreesToRadians(quaySlope));
    const slopeLengthOverWaterY = slopeLengthOverWater * Math.sin(degreesToRadians(quaySlope));
    const doubleMaterialWidth = project.data?.material?.category == EMaterialCategory.RenoMac ? 2 * convertToPixel : 1 * convertToPixel;
    const structureOverQuayHeight = 40;
    const distanceIndicatorCanvas = 35;

    const canvasRef = React.useRef<HTMLDivElement>(null);

    let [reset, setReset] = React.useState<boolean>();
    let [images, setImages] = React.useState<ISrcImage>();

    const getWidth = () => {
        switch (typeOfQuay) {
            case ETypeOfQuay.Vertical:
                if (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                    return quayHorizontalWidth + Math.max(widthOfTheProtectionW, propellerDistanceFromQuayL + engineWidth) + offsetMaterial;
                } else if (
                    (typeOfPropeller == ETypeOfPropeller.BowThruster && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) ||
                    (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Parallel)
                ) {
                    return quayHorizontalWidth + Math.max(widthOfTheProtectionW, distanceBetweenVesselAndQuayBquay + vesselBeamBs) + offsetMaterial;
                }
                break;
            default:
                if (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                    return (
                        quayHorizontalWidth +
                        Math.max(
                            widthOfTheProtectionW + slopeLengthUnderWaterX + slopeLengthOverWaterX + offsetMaterial,
                            0.7 * slopeLengthUnderWaterX + slopeLengthOverWaterX + propellerDistanceFromQuayL + engineWidth,
                        )
                    );
                } else if (
                    (typeOfPropeller == ETypeOfPropeller.BowThruster && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) ||
                    (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Parallel)
                ) {
                    return (
                        quayHorizontalWidth +
                        Math.max(
                            widthOfTheProtectionW + slopeLengthUnderWaterX + slopeLengthOverWaterX + offsetMaterial,
                            0.7 * slopeLengthUnderWaterX + slopeLengthOverWaterX + distanceBetweenVesselAndQuayBquay + vesselBeamBs,
                        )
                    );
                }
                break;
        }
    };

    const getHeight = () => overWaterHeight + minimumWaterDepth + groundHeight;

    const setAngleIndicator = (x: number, y: number, angle: number) => [
        new Konva.Text({
            x: x + 35,
            y: y - 20,
            width: angle % 1 > 0.1 ? 48 : 35,
            height: 20,
            text: `${angle}°`,
            fontSize: 18,
            fontFamily: 'Calibri',
            fill: color.text,
            align: 'center',
            verticalAlign: 'middle',
            sceneFunc: (context, shape) => {
                // Set the background color
                context.fillStyle = color.backgroundText;
                // Draw the background rectangle
                context.fillRect(0, 0, shape.width(), shape.height());
                // Render the text
                (shape as Konva.Text)._sceneFunc(context);
            },
        }),
        new Konva.Wedge({
            x,
            y,
            radius: 30,
            angle,
            stroke: color.lineIndicator,
            strokeWidth: 1,
            rotation: -angle,
        }),
    ];

    const setRectIndicator = (x: number, y: number, width: number, text: string) => [
        new Konva.Text({
            x: x + (width - 150) / 2,
            y: y + 35,
            width: 150,
            height: 20,
            text,
            fontSize: 18,
            fontFamily: 'Calibri',
            fill: color.text,
            align: 'center',
            verticalAlign: 'middle',
            sceneFunc: (context, shape) => {
                //Set the background color
                context.fillStyle = color.backgroundText;
                //Draw the background rectangle
                context.fillRect(0, 0, shape.width(), shape.height());
                //Render the text
                (shape as Konva.Text)._sceneFunc(context);
            },
        }),
        new Konva.Rect({
            x,
            y,
            width,
            height: 35,
            fill: 'transparent',
            stroke: color.lineIndicator,
            strokeWidth: 1.5,
            dash: [5, 5],
        }),
    ];

    const setWedgeIndicator = (x: number, y: number, text: number) => [
        new Konva.Text({
            x: x - 25,
            y: y - 35,
            width: 50,
            height: 20,
            text: text.toString(),
            fontSize: 18,
            fontFamily: 'Calibri',
            fill: color.text,
            align: 'center',
            verticalAlign: 'middle',
            sceneFunc: (context, shape) => {
                // Set the background color
                context.fillStyle = color.backgroundText;
                // Draw the background rectangle
                context.fillRect(0, 0, shape.width(), shape.height());
                // Render the text
                (shape as Konva.Text)._sceneFunc(context);
            },
        }),
        new Konva.Wedge({
            x,
            y,
            radius: 13,
            angle: 60,
            fill: color.lineIndicator,
            stroke: color.lineIndicator,
            strokeWidth: 2,
            rotation: -120,
        }),
    ];

    const setIndicatorX = (x: number, y: number, value: number) => [
        new Konva.Line({
            points: [x, y - 4, x, y + 4],
            stroke: color.lineIndicator,
            strokeWidth: 2,
        }),
        new Konva.Line({
            points: [x + value, y - 4, x + value, y + 4],
            stroke: color.lineIndicator,
            strokeWidth: 2,
        }),
        new Konva.Line({
            points: [x, y, x + value, y],
            stroke: color.lineIndicator,
            strokeWidth: 2,
        }),
        new Konva.Text({
            x: x + value / 2 - 15,
            y: y + 2,
            width: (value / convertToPixel) % 1 > 0.1 ? 45 : 30,
            height: 20,
            text: (value / convertToPixel) % 1 > 0.1 ? (value / convertToPixel).toFixed(2) : Math.floor(value / convertToPixel).toString(),
            fontSize: 18,
            fontFamily: 'Calibri',
            fill: color.text,
            align: 'center',
            verticalAlign: 'middle',
            sceneFunc: (context, shape) => {
                context.fillStyle = color.backgroundText;
                context.fillRect(0, 0, shape.width(), shape.height());
                (shape as Konva.Text)._sceneFunc(context);
            },
        }),
    ];

    const setIndicatorY = (x: number, y: number, value: number) => [
        new Konva.Line({
            points: [x - 4, y, x + 4, y],
            stroke: color.lineIndicator,
            strokeWidth: 2,
        }),
        new Konva.Line({
            points: [x - 4, y + value, x + 4, y + value],
            stroke: color.lineIndicator,
            strokeWidth: 2,
        }),
        new Konva.Line({
            points: [x, y, x, y + value],
            stroke: color.lineIndicator,
            strokeWidth: 2,
        }),
        new Konva.Text({
            x: (value / convertToPixel) % 1 > 0.1 ? x - 50 : x - 30,
            y: y + value / 2 - 10,
            width: (value / convertToPixel) % 1 > 0.1 ? 45 : 30,
            height: 20,
            text: (value / convertToPixel) % 1 > 0.1 ? (value / convertToPixel).toFixed(2) : Math.floor(value / convertToPixel).toString(),
            fontSize: 18,
            fontFamily: 'Calibri',
            fill: 'black',
            align: 'center',
            verticalAlign: 'middle',
            sceneFunc: (context, shape) => {
                context.fillStyle = color.backgroundText;
                context.fillRect(0, 0, shape.width(), shape.height());
                (shape as Konva.Text)._sceneFunc(context);
            },
        }),
    ];

    const handleZoom = (direction: number, scaleBy: number) => {
        var oldScale = stage.scaleX();
        var mousePointTo = {
            x: (stage.width() / 2 - stage.x()) / oldScale,
            y: (stage.height() / 2 - stage.y()) / oldScale,
        };
        var newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
        stage.scale({ x: newScale, y: newScale });
        var newPos = {
            x: stage.width() / 2 - mousePointTo.x * newScale,
            y: stage.height() / 2 - mousePointTo.y * newScale,
        };
        stage.position(newPos);
    };

    const draw = () => {
        stage = new Konva.Stage({
            container: canvasRef.current!,
            width: canvasRef.current?.clientWidth!,
            height: canvasRef.current?.clientHeight!,
        });
        scaleBy = (0.85 * stage.width()) / getWidth()!;
        group = new Konva.Group({
            x: (stage.width() - getWidth()!) / 2,
            y: (stage.height() - getHeight()!) / 2,
            width: getWidth()!,
            height: getHeight(),
            draggable: true,
        });

        var rect1 = new Konva.Rect({
            x: 0,
            y: 0,
            width: group.width(),
            height: group.height(),
            fill: 'green',
            stroke: 'black',
            strokeWidth: 4,
        });

        var water = [
            new Konva.Rect({
                x: 0,
                y: overWaterHeight,
                width: group.width(),
                height: minimumWaterDepth,
                fill: color.water,
                opacity: 0.5,
            }),
            new Konva.Line({
                points: [0, overWaterHeight, group.width(), overWaterHeight],
                stroke: color.lineWater,
                strokeWidth: 2,
            }),
        ];

        var ground = new Konva.Rect({
            x: 0,
            y: overWaterHeight + minimumWaterDepth,
            width: group.width(),
            height: groundHeight,
            fillPatternImage: images!.patternEarth as HTMLImageElement,
        });

        var quay = (() => {
            switch (typeOfQuay) {
                case ETypeOfQuay.Vertical:
                    startQuayX = group.width() - quayHorizontalWidth;
                    return new Konva.Rect({
                        x: startQuayX,
                        y: offsetOverQuay,
                        width: quayHorizontalWidth,
                        height: slopeLengthOverWaterY + slopeLengthUnderWaterY + groundHeight,
                        fillPatternImage: images!.patternQuay as HTMLImageElement,
                    });
                default:
                    startQuayX = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX;
                    return new Konva.Line({
                        points: [
                            startQuayX,
                            overWaterHeight + minimumWaterDepth,
                            startQuayX,
                            overWaterHeight + minimumWaterDepth + groundHeight,
                            group.width(),
                            overWaterHeight + minimumWaterDepth + groundHeight,
                            group.width(),
                            offsetOverQuay,
                            group.width() - quayHorizontalWidth,
                            offsetOverQuay,
                            group.width() - quayHorizontalWidth,
                            offsetOverQuay,
                        ],
                        fillPatternImage: images!.patternQuay as HTMLImageElement,
                        closed: true,
                    });
            }
        })();

        var material = (() => {
            switch (typeOfQuay) {
                case ETypeOfQuay.Vertical:
                    startMaterialX = group.width() - quayHorizontalWidth - widthOfTheProtectionW;
                    return new Konva.Rect({
                        x: startMaterialX,
                        y: overWaterHeight + minimumWaterDepth - thicknessT,
                        width: widthOfTheProtectionW,
                        height: thicknessT,
                        fillPatternImage: images!.patternMaterial ? (images!.patternMaterial as HTMLImageElement) : undefined,
                        fill: images!.patternMaterial ? '' : project.data?.material?.color,
                    });
                default:
                    startMaterialX = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX - widthOfTheProtectionW;
                    return new Konva.Line({
                        points: [
                            startMaterialX,
                            overWaterHeight + minimumWaterDepth - thicknessT,
                            startMaterialX + widthOfTheProtectionW,
                            overWaterHeight + minimumWaterDepth - thicknessT,
                            group.width() - quayHorizontalWidth - thicknessT,
                            offsetOverQuay,
                            group.width() - quayHorizontalWidth,
                            offsetOverQuay,
                            startMaterialX + widthOfTheProtectionW + thicknessT,
                            overWaterHeight + minimumWaterDepth,
                            startMaterialX,
                            overWaterHeight + minimumWaterDepth,
                        ],
                        fillPatternImage: images!.patternMaterial ? (images!.patternMaterial as HTMLImageElement) : undefined,
                        fill: images!.patternMaterial ? '' : project.data?.material?.color,
                        closed: true,
                    });
            }
        })();

        var doubleMaterial = (() => {
            if (typeOfQuay == ETypeOfQuay.Vertical && project.data?.geometry?.doubleLayerNearToTheQuayWall) {
                return new Konva.Rect({
                    x: group.width() - quayHorizontalWidth - doubleMaterialWidth,
                    y: overWaterHeight + minimumWaterDepth - thicknessT - doubleMaterialthicknessT,
                    width: doubleMaterialWidth,
                    height: doubleMaterialthicknessT,
                    fillPatternImage: images!.patternDoubleMaterial as HTMLImageElement,
                });
            } else {
                return new Konva.Rect({});
            }
        })();

        var structureOverQuay = (() => {
            let x = [];
            switch (typeOfQuay) {
                case ETypeOfQuay.Vertical:
                    startStructureOverQuayX = group.width() - quayHorizontalWidth;
                    x.push(
                        new Konva.Rect({
                            x: group.width() - quayHorizontalWidth,
                            y: offsetOverQuay - structureOverQuayHeight,
                            width: quayHorizontalWidth,
                            height: structureOverQuayHeight,
                            fill: color.structure,
                        }),
                    );
                    break;
                default:
                    startStructureOverQuayX = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - 0.7 * slopeLengthUnderWaterX;
                    x.push(
                        new Konva.Rect({
                            x: group.width() - quayHorizontalWidth - slopeLengthOverWaterX - 0.7 * slopeLengthUnderWaterX,
                            y: offsetOverQuay - structureOverQuayHeight,
                            width: quayHorizontalWidth + slopeLengthOverWaterX + 0.7 * slopeLengthUnderWaterX,
                            height: structureOverQuayHeight,
                            fill: color.structure,
                        }),
                    );
                    break;
            }
            let width = 80;
            let height = 200;
            x.push(
                new Konva.Rect({
                    x: group.width() - quayHorizontalWidth + 10,
                    y: offsetOverQuay - structureOverQuayHeight - height,
                    width,
                    height,
                    fillPatternImage: images!.structureOverQuayImg as HTMLImageElement,
                    fillPatternScaleX: width / (images!.structureOverQuayImg as HTMLImageElement).width,
                    fillPatternScaleY: height / (images!.structureOverQuayImg as HTMLImageElement).height!,
                }),
            );
            return x;
        })();

        var piles = (() => {
            switch (typeOfQuay) {
                case ETypeOfQuay.SlopedWithPiles:
                    var numberOfPiles = (slopeLengthOverWaterX + 0.7 * slopeLengthUnderWaterX) / (pileDiameter + distanceBetweenPileAxis);
                    var arr: Rect[] = [];
                    for (let index = 0; index < numberOfPiles; index++) {
                        let lengthStructureOverSlope = slopeLengthOverWaterX + 0.7 * slopeLengthUnderWaterX;
                        arr.push(
                            new Konva.Rect({
                                x: group.width() - lengthStructureOverSlope - quayHorizontalWidth + (index * lengthStructureOverSlope) / numberOfPiles,
                                y: offsetOverQuay + 5,
                                width: lengthStructureOverSlope / numberOfPiles - 6,
                                height: 10,
                                fill: color.piles,
                            }),
                        );
                        let x =
                            group.width() -
                            quayHorizontalWidth -
                            lengthStructureOverSlope +
                            (index * lengthStructureOverSlope) / numberOfPiles +
                            (lengthStructureOverSlope / numberOfPiles - 6 - pileDiameter) / 2;
                        if (index == 0) startFirstPileX = x;

                        arr.push(
                            new Konva.Rect({
                                x,
                                y: offsetOverQuay + 10,
                                width: pileDiameter,
                                height: quayOverWaterHeight + minimumWaterDepth,
                                fill: color.piles,
                            }),
                        );
                    }
                    return arr;
                default:
                    return [new Konva.Rect({})];
            }
        })();

        var ship = (() => {
            let arr = [];
            if (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                let width = 0;
                if (typeOfQuay == ETypeOfQuay.Vertical) width = group.width() - quayHorizontalWidth - distanceBetweenVesselAndQuayBquay;
                else width = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - 0.7 * slopeLengthUnderWaterX - distanceBetweenVesselAndQuayBquay;
                let height = structureOverQuayHeight + slopeLengthOverWaterY + draught;
                startShipX = 0;

                let t = 0;
                if (typeOfQuay == ETypeOfQuay.Vertical) t = group.width() - quayHorizontalWidth - propellerDistanceFromQuayL - engineWidth / 2;
                else t = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX - horizontalDistanceBetweenPropellerAxisAndTheSlope - engineWidth / 2;

                arr.push(
                    new Konva.Rect({
                        x: 0,
                        y: offsetOverQuay - (3 / 4) * height,
                        width: (3 / 4) * width,
                        height: (3 / 4) * height,
                        fillPatternImage: images!.containerTransversal as HTMLImageElement,
                        fillPatternScaleX: ((3 / 4) * width) / (images!.containerTransversal as HTMLImageElement).width,
                        fillPatternScaleY: ((3 / 4) * height) / (images!.containerTransversal as HTMLImageElement).height,
                    }),
                    new Konva.Shape({
                        x: 0,
                        y: 0,
                        sceneFunc: function (context, shape) {
                            context.beginPath();
                            context.moveTo(0, offsetOverQuay - structureOverQuayHeight);
                            context.lineTo(width, offsetOverQuay - structureOverQuayHeight);
                            context.lineTo(width, offsetOverQuay + quayOverWaterHeight);
                            context.quadraticCurveTo(
                                t,
                                overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2 - 40,
                                t,
                                overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                            );
                            context.lineTo(t, height);
                            context.lineTo(0, height);
                            context.closePath();
                            context.fillStyle = color.structure;
                            context.fill();
                            context.stroke();
                        },
                    }),
                    new Konva.Rect({
                        x: 0,
                        y: offsetOverQuay - structureOverQuayHeight,
                        width: width,
                        height: structureOverQuayHeight / 2,
                        fill: 'black',
                    }),
                    new Konva.Rect({
                        x: 0,
                        y: offsetOverQuay - structureOverQuayHeight / 2,
                        width: width,
                        height: quayOverWaterHeight + structureOverQuayHeight / 2,
                        fill: '#5C9BAA',
                    }),
                );
            } else if (typeOfPropeller == ETypeOfPropeller.BowThruster && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                let x = 0;
                if (typeOfQuay == ETypeOfQuay.Vertical) x = group.width() - quayHorizontalWidth - distanceBetweenVesselAndQuayBquay - vesselBeamBs;
                else x = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - 0.7 * slopeLengthUnderWaterX - distanceBetweenVesselAndQuayBquay - vesselBeamBs;
                let height = structureOverQuayHeight + slopeLengthOverWaterY + draught;
                startShipX = x;
                arr.push(
                    new Konva.Rect({
                        x: x + 20,
                        y: offsetOverQuay - (3 / 4) * height,
                        width: vesselBeamBs - 40,
                        height: (3 / 4) * height,
                        fillPatternImage: images!.containerParallel as HTMLImageElement,
                        fillPatternRepeat: 'no-repeat',
                        fillPatternScaleX: (vesselBeamBs - 40) / (images!.containerParallel as HTMLImageElement).width,
                        fillPatternScaleY: ((3 / 4) * height) / (images!.containerParallel as HTMLImageElement).height,
                    }),
                    new Konva.Rect({
                        x,
                        y: offsetOverQuay - structureOverQuayHeight,
                        width: vesselBeamBs,
                        height,
                        fillPatternImage: images!.shipParallel as HTMLImageElement,
                        fillPatternScaleX: vesselBeamBs / (images!.shipParallel as HTMLImageElement).width,
                        fillPatternScaleY: height / (images!.shipParallel as HTMLImageElement).height,
                    }),
                );
            } else if (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Parallel) {
                let x = 0;
                if (typeOfQuay == ETypeOfQuay.Vertical) x = group.width() - quayHorizontalWidth - distanceBetweenVesselAndQuayBquay - vesselBeamBs;
                else x = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - 0.7 * slopeLengthUnderWaterX - distanceBetweenVesselAndQuayBquay - vesselBeamBs;
                let height = structureOverQuayHeight + slopeLengthOverWaterY + draught;
                startShipX = x;
                arr.push(
                    new Konva.Rect({
                        x: x + 20,
                        y: offsetOverQuay - (3 / 4) * height,
                        width: vesselBeamBs - 40,
                        height: (3 / 4) * height,
                        fillPatternImage: images!.containerParallel as HTMLImageElement,
                        fillPatternScaleX: (vesselBeamBs - 40) / (images!.containerParallel as HTMLImageElement).width,
                        fillPatternScaleY: ((3 / 4) * height) / (images!.containerParallel as HTMLImageElement).height,
                    }),
                    new Konva.Rect({
                        x,
                        y: offsetOverQuay - structureOverQuayHeight,
                        width: vesselBeamBs,
                        height,
                        fillPatternImage: images!.shipParallel as HTMLImageElement,
                        fillPatternScaleX: vesselBeamBs / (images!.shipParallel as HTMLImageElement).width,
                        fillPatternScaleY: height / (images!.shipParallel as HTMLImageElement).height,
                    }),
                );
            }
            return arr;
        })();

        var engine = (() => {
            let arr = [];
            if (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                let x = 0;
                if (typeOfQuay == ETypeOfQuay.Vertical) x = group.width() - quayHorizontalWidth - propellerDistanceFromQuayL - engineWidth;
                else x = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX - horizontalDistanceBetweenPropellerAxisAndTheSlope - engineWidth;
                startEngineX = x;
                arr.push(
                    new Konva.Image({
                        image: canvasPropellerLat,
                        x,
                        y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                        width: engineWidth,
                        height: propellerDiameter,
                    }),
                );
                arr.push(
                    new Konva.Rect({
                        x: 0,
                        y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                        // A small widthOfTheProtectionW (even transient while typing) can push x below -engineWidth/2:
                        // a negative width with cornerRadius makes Konva call arc() with a negative radius and throw IndexSizeError
                        width: Math.max(0, x + engineWidth / 2),
                        height: propellerDiameter,
                        fill: color.structure,
                        cornerRadius: 10,
                    }),
                );
            } else if (typeOfPropeller == ETypeOfPropeller.BowThruster && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                let x = 0;
                if (typeOfQuay == ETypeOfQuay.Vertical) x = group.width() - quayHorizontalWidth - propellerDistanceFromQuayL - engineWidth;
                else x = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX - horizontalDistanceBetweenPropellerAxisAndTheSlope - engineWidth;
                startEngineX = x;
                arr.push(
                    new Konva.Rect({
                        x: ship![1].x(),
                        y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                        width: ship![1].width(),
                        height: propellerDiameter,
                        fill: '#78b8aa',
                        globalCompositeOperation: 'source-atop',
                    }),
                );
                arr.push(
                    new Konva.Rect({
                        x: x + engineWidth / 5,
                        y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                        width: engineWidth / 2.4,
                        height: propellerDiameter / 2,
                        fill: color.structure,
                    }),
                );
                arr.push(
                    new Konva.Image({
                        image: canvasPropellerLat,
                        x,
                        y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                        width: engineWidth,
                        height: propellerDiameter,
                    }),
                );
            } else if (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Parallel) {
                if (numberOfPropellers == ENumberOfPropellers.Single) {
                    let x = 0;
                    if (typeOfQuay == ETypeOfQuay.Vertical) x = group.width() - quayHorizontalWidth - distanceBetweenVesselAndQuayBquay - vesselBeamBs / 2 - engineWidth / 2;
                    else x = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - 0.7 * slopeLengthUnderWaterX - distanceBetweenVesselAndQuayBquay - vesselBeamBs / 2 - engineWidth / 2;
                    startEngineX = x;
                    arr.push(
                        new Konva.Image({
                            image: canvasPropellerFront,
                            x,
                            y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                            width: engineWidth,
                            height: propellerDiameter,
                        }),
                    );
                } else {
                    [0, 1].map(el => {
                        let x = 0;
                        if (typeOfQuay == ETypeOfQuay.Vertical) x = group.width() - quayHorizontalWidth - distanceBetweenVesselAndQuayBquay - vesselBeamBs / 2 - el * engineWidth;
                        else x = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - 0.7 * slopeLengthUnderWaterX - distanceBetweenVesselAndQuayBquay - vesselBeamBs / 2 - el * engineWidth;
                        if (el == 1) startEngineX = x;
                        arr.push(
                            new Konva.Image({
                                image: canvasPropellerFront,
                                x,
                                y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                                width: engineWidth,
                                height: propellerDiameter,
                            }),
                        );
                    });
                }
            }
            return arr;
        })();

        var waterFlow = (() => {
            let arr = [];
            if (result.status == EReduxState.fulfilled) {
                if (propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                    let arrows = [0, 1, 2, 3].map(
                        x =>
                            new Konva.Arrow({
                                x: startEngineX,
                                y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2 + (propellerDiameter / 4) * x + propellerDiameter / 8,
                                points: [0, 0, propellerDiameter / 3, 0],
                                pointerLength: propellerDiameter / 16,
                                pointerWidth: propellerDiameter / 16,
                                fill: 'white',
                                stroke: 'white',
                                globalCompositeOperation: 'destination-over',
                                strokeWidth: propellerDiameter / 20,
                            }),
                    );
                    if (typeOfQuay == ETypeOfQuay.Vertical) {
                        let x = startEngineX;
                        let r = startEngineX;
                        if (typeOfPropeller == ETypeOfPropeller.BowThruster) {
                            x =
                                ship![1].x() +
                                vesselBeamBs / 2 +
                                getDistanceFromVertex(
                                    [ship![1].x() + vesselBeamBs / 2, overWaterHeight + draught],
                                    [ship![1].x() + vesselBeamBs, ship![1].y()],
                                    overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                                )!;
                            r =
                                ship![1].x() +
                                vesselBeamBs / 2 +
                                getDistanceFromVertex(
                                    [ship![1].x() + vesselBeamBs / 2, overWaterHeight + draught],
                                    [ship![1].x() + vesselBeamBs, ship![1].y()],
                                    overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell + propellerDiameter / 2,
                                )!;
                        }
                        startZoneOfErosionX = group.width() - quayHorizontalWidth - 150;
                        zoneOfErosionWidth = 150;
                        arr.push(
                            ...arrows,
                            new Konva.Shape({
                                x: 0,
                                y: 0,
                                sceneFunc: function (context, shape) {
                                    context.beginPath();
                                    context.moveTo(x, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2);
                                    context.quadraticCurveTo(
                                        group.width() - quayHorizontalWidth - 20,
                                        overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2 + 10,
                                        group.width() - quayHorizontalWidth,
                                        overWaterHeight + (minimumWaterDepth - propellerDiameter) / 5,
                                    );
                                    context.lineTo(group.width() - 1, overWaterHeight + (minimumWaterDepth - propellerDiameter) / 5);
                                    context.lineTo(group.width() - 1, overWaterHeight + minimumWaterDepth);
                                    context.lineTo(group.width() - quayHorizontalWidth - 100, overWaterHeight + minimumWaterDepth);
                                    context.quadraticCurveTo(
                                        group.width(),
                                        overWaterHeight + minimumWaterDepth - 50,
                                        r,
                                        overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell + propellerDiameter / 2,
                                    );
                                    context.closePath();
                                    context.fillStyle = color.waterFlow;
                                    context.globalCompositeOperation = 'destination-over';
                                    context.fill();
                                    context.stroke();
                                },
                            }),
                        );
                    } else {
                        let x = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX - horizontalDistanceBetweenPropellerAxisAndTheSlope;
                        let r = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX - horizontalDistanceBetweenPropellerAxisAndTheSlope;
                        if (typeOfPropeller == ETypeOfPropeller.BowThruster) {
                            x =
                                ship![1].x() +
                                vesselBeamBs / 2 +
                                getDistanceFromVertex(
                                    [ship![1].x() + vesselBeamBs / 2, overWaterHeight + draught],
                                    [ship![1].x() + vesselBeamBs, ship![1].y()],
                                    overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                                )!;
                            r =
                                ship![1].x() +
                                vesselBeamBs / 2 +
                                getDistanceFromVertex(
                                    [ship![1].x() + vesselBeamBs / 2, overWaterHeight + draught],
                                    [ship![1].x() + vesselBeamBs, ship![1].y()],
                                    overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell + propellerDiameter / 2,
                                )!;
                        }
                        arr.push(
                            ...arrows,
                            new Konva.Shape({
                                x: 0,
                                y: 0,
                                sceneFunc: function (context, shape) {
                                    context.beginPath();
                                    context.moveTo(x, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2);
                                    context.quadraticCurveTo(
                                        group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX / 2,
                                        overWaterHeight + (minimumWaterDepth - propellerDiameter) / 5 + 60,
                                        group.width() - quayHorizontalWidth,
                                        overWaterHeight + (minimumWaterDepth - propellerDiameter) / 100,
                                    );
                                    context.lineTo(group.width() - 1, overWaterHeight + (minimumWaterDepth - propellerDiameter) / 100);
                                    context.lineTo(group.width() - 1, overWaterHeight + minimumWaterDepth - (minimumWaterDepth - propellerDiameter) / 100);
                                    context.lineTo(group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX, overWaterHeight + minimumWaterDepth);
                                    context.quadraticCurveTo(
                                        group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX + 30,
                                        overWaterHeight + minimumWaterDepth - (minimumWaterDepth - propellerDiameter) / 3,
                                        r,
                                        overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell + propellerDiameter / 2,
                                    );
                                    context.closePath();
                                    context.fillStyle = color.waterFlow;
                                    context.globalCompositeOperation = 'destination-over';
                                    context.fill();
                                    context.stroke();
                                },
                            }),
                            //vertice
                            // new Konva.Rect({
                            //     x: ship![1].x() + vesselBeamBs / 2,
                            //     y: overWaterHeight + draught,
                            //     width: 5,
                            //     height: 5,
                            //     fill: 'red',
                            // }),
                            //punto
                            // new Konva.Rect({
                            //     x: ship![1].x() + vesselBeamBs,
                            //     y: ship![1].y(),
                            //     width: 5,
                            //     height: 5,
                            //     fill: 'red',
                            // }),
                            //punto 1
                            // new Konva.Rect({
                            //     x:
                            //         ship![1].x() +
                            //         vesselBeamBs / 2 +
                            //         getDistanceFromVertex(
                            //             [ship![1].x() + vesselBeamBs / 2, overWaterHeight + draught],
                            //             [ship![1].x() + vesselBeamBs, ship![1].y()],
                            //             overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell + propellerDiameter / 2,
                            //         )!,
                            //     y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell + propellerDiameter / 2,
                            //     width: 5,
                            //     height: 5,
                            //     fill: 'red',
                            // }),
                            //punto 2
                            // new Konva.Rect({
                            //     x:
                            //         ship![1].x() +
                            //         vesselBeamBs / 2 +
                            //         getDistanceFromVertex(
                            //             [ship![1].x() + vesselBeamBs / 2, overWaterHeight + draught],
                            //             [ship![1].x() + vesselBeamBs, ship![1].y()],
                            //             overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                            //         )!,
                            //     y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                            //     width: 5,
                            //     height: 5,
                            //     fill: 'red',
                            // }),
                        );
                    }
                    var switchArr = true;
                    var anim = new Konva.Animation(function (frame) {
                        arrows.map((arrow, i) => {
                            let condition = switchArr ? i == 0 || i == 3 : i == 1 || i == 2;
                            if (condition) {
                                var x = arrow.x() + 8;
                                if (x > group.width() - quayHorizontalWidth / 2) {
                                    x = startEngineX;
                                    if (i % 2 == 0) switchArr = !switchArr;
                                }
                                arrow.x(x);
                            }
                        });
                    }, layer);

                    anim.start();
                } else {
                    if (numberOfPropellers == ENumberOfPropellers.Single) {
                        let x = 0;
                        if (typeOfQuay == ETypeOfQuay.Vertical) x = group.width() - quayHorizontalWidth - distanceBetweenVesselAndQuayBquay - vesselBeamBs / 2 - engineWidth / 2;
                        else x = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - 0.7 * slopeLengthUnderWaterX - distanceBetweenVesselAndQuayBquay - vesselBeamBs / 2 - engineWidth / 2;
                        startZoneOfErosionX = x - (vesselBeamBs - propellerDiameter) / 1.8;
                        zoneOfErosionWidth = x + propellerDiameter + (vesselBeamBs - propellerDiameter) / 1.8 - startZoneOfErosionX;
                        let arrows = [0, 1, 2, 3].map(
                            x =>
                                new Konva.Arrow({
                                    x: startEngineX + (propellerDiameter / 4) * x + propellerDiameter / 8,
                                    y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                                    points: [0, 0, 0, propellerDiameter / 3],
                                    pointerLength: propellerDiameter / 16,
                                    pointerWidth: propellerDiameter / 16,
                                    fill: 'white',
                                    stroke: 'white',
                                    strokeWidth: propellerDiameter / 20,
                                }),
                        );
                        arr.push(
                            new Konva.Shape({
                                x: 0,
                                y: 0,
                                sceneFunc: function (context, shape) {
                                    context.beginPath();
                                    context.moveTo(x, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell);
                                    context.lineTo(x + propellerDiameter, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell);
                                    context.quadraticCurveTo(
                                        x + propellerDiameter,
                                        overWaterHeight + minimumWaterDepth,
                                        x + propellerDiameter + (vesselBeamBs - propellerDiameter) / 1.8,
                                        overWaterHeight + minimumWaterDepth,
                                    );
                                    context.lineTo(x - (vesselBeamBs - propellerDiameter) / 1.8, overWaterHeight + minimumWaterDepth);
                                    context.quadraticCurveTo(x, overWaterHeight + minimumWaterDepth, x, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell);

                                    context.closePath();
                                    context.fillStyle = color.waterFlow;
                                    context.fill();
                                    context.stroke();
                                },
                            }),
                            ...arrows,
                        );
                        var switchArr = true;
                        var anim = new Konva.Animation(function (frame) {
                            arrows.map((arrow, i) => {
                                let condition = switchArr ? i == 0 || i == 3 || i == 4 || i == 7 : i == 1 || i == 2 || i == 5 || i == 6;
                                if (condition) {
                                    var y = arrow.y() + 4;
                                    if (y > overWaterHeight + minimumWaterDepth) {
                                        y = overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2;
                                        if (i % 2 == 0) switchArr = !switchArr;
                                    }
                                    arrow.y(y);
                                }
                            });
                        }, layer);

                        anim.start();
                    } else {
                        let x = 0;
                        if (typeOfQuay == ETypeOfQuay.Vertical) x = group.width() - quayHorizontalWidth - distanceBetweenVesselAndQuayBquay - vesselBeamBs / 2 - engineWidth;
                        else x = group.width() - quayHorizontalWidth - slopeLengthOverWaterX - 0.7 * slopeLengthUnderWaterX - distanceBetweenVesselAndQuayBquay - vesselBeamBs / 2 - engineWidth;
                        startZoneOfErosionX = x - (vesselBeamBs - 2 * propellerDiameter) / 1.8;
                        zoneOfErosionWidth = x + 2 * propellerDiameter + (vesselBeamBs - 2 * propellerDiameter) / 1.8 - startZoneOfErosionX;
                        let arrows = [0, 1, 2, 3, 4, 5, 6, 7].map(
                            x =>
                                new Konva.Arrow({
                                    x: startEngineX + (propellerDiameter / 4) * x + propellerDiameter / 8,
                                    y: overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2,
                                    points: [0, 0, 0, propellerDiameter / 3],
                                    pointerLength: propellerDiameter / 16,
                                    pointerWidth: propellerDiameter / 16,
                                    fill: 'white',
                                    stroke: 'white',
                                    strokeWidth: propellerDiameter / 20,
                                }),
                        );
                        arr.push(
                            new Konva.Shape({
                                x: 0,
                                y: 0,
                                sceneFunc: function (context, shape) {
                                    context.beginPath();
                                    context.moveTo(x, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell);
                                    context.lineTo(x + 2 * propellerDiameter, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell);
                                    context.quadraticCurveTo(
                                        x + 2 * propellerDiameter,
                                        overWaterHeight + minimumWaterDepth,
                                        x + 2 * propellerDiameter + (vesselBeamBs - 2 * propellerDiameter) / 1.8,
                                        overWaterHeight + minimumWaterDepth,
                                    );
                                    context.lineTo(x - (vesselBeamBs - 2 * propellerDiameter) / 1.8, overWaterHeight + minimumWaterDepth);
                                    context.quadraticCurveTo(x, overWaterHeight + minimumWaterDepth, x, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell);
                                    context.closePath();
                                    context.fillStyle = color.waterFlow;
                                    context.fill();
                                    context.stroke();
                                },
                            }),
                            ...arrows,
                        );

                        var switchArr = true;
                        var anim = new Konva.Animation(function (frame) {
                            arrows.map((arrow, i) => {
                                let condition = switchArr ? i == 0 || i == 3 || i == 4 || i == 7 : i == 1 || i == 2 || i == 5 || i == 6;
                                if (condition) {
                                    var y = arrow.y() + 4;
                                    if (y > overWaterHeight + minimumWaterDepth) {
                                        y = overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2;
                                        if (i % 2 == 0) switchArr = !switchArr;
                                    }
                                    arrow.y(y);
                                }
                            });
                        }, layer);

                        anim.start();
                    }
                }
            } else {
                arr.push(new Konva.Rect({}));
            }
            return arr;
        })();

        stage.on('wheel', (e: any) => {
            e.evt.preventDefault();
            let direction = e.evt.deltaY > 0 ? 1 : -1;
            handleZoom(direction, 0.9);
        });

        const indicator = (() => {
            let arr = [];
            //minimumWaterDepth
            arr.push(...setIndicatorY(-distanceIndicatorCanvas * (startShipX == 0 ? 2 : 1), overWaterHeight, minimumWaterDepth));
            //waterLevel
            arr.push(...setWedgeIndicator(40, overWaterHeight - designWaterLevel, designWaterLevel));
            //dredgingLevel
            arr.push(...setWedgeIndicator(40, overWaterHeight + minimumWaterDepth, designDredgingLevel));
            //distanceBetweenVesselAndQuayBquay
            arr.push(...setIndicatorX(startStructureOverQuayX - distanceBetweenVesselAndQuayBquay, offsetOverQuay - structureOverQuayHeight / 2, distanceBetweenVesselAndQuayBquay));
            //widthOfTheProtectionW
            arr.push(...setIndicatorX(startMaterialX, overWaterHeight + minimumWaterDepth + groundHeight + distanceIndicatorCanvas * (doubleMaterialthicknessT ? 2 : 1), widthOfTheProtectionW));
            //doubleMaterialthicknessT
            if (doubleMaterialthicknessT) {
                //doubleMaterialthicknessT
                arr.push(
                    ...setIndicatorX(startMaterialX + widthOfTheProtectionW - doubleMaterialWidth, overWaterHeight + minimumWaterDepth + groundHeight + distanceIndicatorCanvas, doubleMaterialWidth),
                );
            }

            if (typeOfQuay == ETypeOfQuay.Sloped) {
                //quaySlope
                arr.push(...setAngleIndicator(group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX + thicknessT + 1, overWaterHeight + minimumWaterDepth, quaySlope));
                //slopeLength
                arr.push(
                    ...setIndicatorX(
                        group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX,
                        overWaterHeight + minimumWaterDepth + groundHeight + distanceIndicatorCanvas,
                        slopeLengthOverWaterX + slopeLengthUnderWaterX,
                    ),
                );
            } else if (typeOfQuay == ETypeOfQuay.SlopedWithPiles) {
                //quaySlope
                arr.push(...setAngleIndicator(group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX + thicknessT + 1, overWaterHeight + minimumWaterDepth, quaySlope));
                //slopeLength
                arr.push(
                    ...setIndicatorX(
                        group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX,
                        overWaterHeight + minimumWaterDepth + groundHeight + distanceIndicatorCanvas,
                        slopeLengthOverWaterX + slopeLengthUnderWaterX,
                    ),
                );
                //distanceBetweenPileAxis
                arr.push(...setIndicatorX(startFirstPileX + pileDiameter, overWaterHeight + 30, distanceBetweenPileAxis));
            }

            //draught
            arr.push(...setIndicatorY(startShipX - distanceIndicatorCanvas / 2, overWaterHeight, draught));

            //propellerDiameter
            arr.push(
                ...setIndicatorY(startEngineX - distanceIndicatorCanvas, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell - propellerDiameter / 2, propellerDiameter),
            );

            if (result.status == EReduxState.fulfilled) {
                //UKC
                arr.push(...setIndicatorY(startShipX + (startShipX == 0 ? -distanceIndicatorCanvas / 2 : vesselBeamBs / 2), overWaterHeight + draught, underKeelClearance));
            }

            if (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Parallel) {
                //ZoneOfErosion
                if (result.status == EReduxState.fulfilled) {
                    arr.push(...setRectIndicator(startZoneOfErosionX, overWaterHeight + minimumWaterDepth - 2, zoneOfErosionWidth, t('zoneOfErosion')));
                }
            } else if (propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                if (typeOfQuay == ETypeOfQuay.Vertical) {
                    //ZoneOfErosion
                    if (result.status == EReduxState.fulfilled) {
                        arr.push(...setRectIndicator(startZoneOfErosionX, overWaterHeight + minimumWaterDepth - 2, zoneOfErosionWidth, t('zoneOfErosion')));
                    }
                    //propellerDistanceFromQuayL
                    arr.push(...setIndicatorX(startEngineX + engineWidth, overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell, propellerDistanceFromQuayL));
                } else {
                    if (result.status == EReduxState.fulfilled) {
                        //XsMax
                        if (slopeAbscissaOfMaximumCurrentVelocityXvMax) {
                            arr.push(
                                ...setIndicatorX(
                                    startQuayX,
                                    overWaterHeight + minimumWaterDepth + distanceIndicatorCanvas,
                                    slopeAbscissaOfMaximumCurrentVelocityXvMax - horizontalDistanceBetweenPropellerAxisAndTheSlope,
                                ),
                            );
                        }

                        //ZoneOfErosion
                        //Necessary with rotation
                        var groupRectIndicator = new Konva.Group({
                            x: startQuayX + ((slopeLengthUnderWater - (slopeLengthUnderWater * 2) / 3) / 2) * Math.cos(degreesToRadians(quaySlope)),
                            y: overWaterHeight + minimumWaterDepth - ((slopeLengthUnderWater - (slopeLengthUnderWater * 2) / 3) / 2) * Math.sin(degreesToRadians(quaySlope)),
                            rotation: -quaySlope,
                        });
                        arr.push(groupRectIndicator.add(...setRectIndicator(0, 0, (slopeLengthUnderWater * 2) / 3, t('zoneOfErosion'))));

                        //Arrow zone of erosion
                        arr.push(
                            new Konva.Arrow({
                                x: startQuayX + (slopeAbscissaOfMaximumCurrentVelocityXvMax! - horizontalDistanceBetweenPropellerAxisAndTheSlope),
                                y:
                                    overWaterHeight +
                                    minimumWaterDepth -
                                    (slopeAbscissaOfMaximumCurrentVelocityXvMax! - horizontalDistanceBetweenPropellerAxisAndTheSlope) * Math.tan(degreesToRadians(quaySlope)) +
                                    30,
                                points: [0, 0, 50, 0],
                                rotation: -quaySlope,
                                pointerLength: 10,
                                pointerWidth: 10,
                                fill: 'black',
                                stroke: 'black',
                                strokeWidth: 2,
                            }),
                        );
                    }
                    //propellerToeDistance
                    arr.push(
                        ...setIndicatorX(
                            group.width() - quayHorizontalWidth - slopeLengthOverWaterX - slopeLengthUnderWaterX - horizontalDistanceBetweenPropellerAxisAndTheSlope,
                            overWaterHeight + draught - verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
                            horizontalDistanceBetweenPropellerAxisAndTheSlope,
                        ),
                    );
                }
            }

            return arr;
        })();

        if (typeOfPropeller == ETypeOfPropeller.MainPropeller && propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Parallel) {
            group.add(...waterFlow!, ...ship!, ...engine, ...water, ...piles, ground, quay, doubleMaterial, ...structureOverQuay, ...indicator, material);
        } else {
            group.add(...ship!, ...engine, ...waterFlow!, ...water, ...piles, ground, quay, doubleMaterial, ...structureOverQuay, ...indicator, material);
        }

        layer.destroyChildren();
        layer.add(group);
        stage.add(layer);

        handleZoom(1, scaleBy);
    };

    React.useEffect(() => {
        var source: ISrcImage = {
            patternQuay,
            patternEarth,
            structureOverQuayImg,
            shipTransversal,
            shipParallel,
            containerParallel,
            containerTransversal,
        };
        let patternMaterial = getTextureByMaterial(project.data!.material!);
        if (patternMaterial) {
            source.patternMaterial = patternMaterial;
        }
        if (project.data!.doubleLayerMaterial) {
            source.patternDoubleMaterial = getTextureByMaterial(project.data!.doubleLayerMaterial);
        }

        loadImages(source, images => setImages(images));
    }, [project.data!.material, project.data!.doubleLayerMaterial]);

    React.useEffect(() => {
        if (matches && images && canvasRef.current?.clientHeight! > 0 && canvasRef.current?.clientWidth! > 0) {
            draw();
            if (result.status == EReduxState.fulfilled && props.export) {
                dispatch(
                    setEngine({
                        index: props.index,
                        engine: stage.toDataURL({ y: -20, pixelRatio: 10 }),
                    }),
                );
            }
        }
    }, [reset, project, images, sizeWindow]);

    return (
        <Stack position='relative' height='100%'>
            <Stack variant='actionEngine' position={'absolute'} right={0} zIndex={1000}>
                <IconButton aria-label='zoomIn' onClick={() => handleZoom(-1, 0.9)}>
                    <ZoomInIcon color='white' />
                </IconButton>
                <IconButton aria-label='zoomOut' onClick={() => handleZoom(1, 0.9)}>
                    <ZoomOutIcon color='white' />
                </IconButton>
                <IconButton aria-label='zoomOutMap' onClick={() => setReset(prev => !prev)}>
                    <ZoomOutMapIcon color='white' />
                </IconButton>
            </Stack>
            <Stack position={'absolute'} left={0} top={0} zIndex={1000}>
                <Card>
                    <CardMedia
                        height={result.status == EReduxState.fulfilled ? 100 : 130}
                        component='img'
                        image={getImageFromScenario(typeOfPropeller, propellerDirectionWithRespectToTheQuay, numberOfPropellers) as string}
                        alt='image-engine'
                    />
                </Card>
            </Stack>
            <Stack ref={canvasRef} style={{ position: 'absolute', height: '100%', width: '100%', backgroundImage: `url(${Grid})` }}></Stack>
        </Stack>
    );
};

export default NewEngine;
